// Owns confirm-modal state for permanent material delete (inactive
// rows only). Kept separate from soft-delete `useMaterialDeletionFlow`
// so the inventory orchestrator can stay under the file-size cap.
import { useCallback, useState } from "react";
import { deleteMaterial as deleteMaterialApi } from "@/lib/manufacturingClient";
import type { MfgMaterial } from "@/lib/manufacturing/types";

type Pending = {
  material: MfgMaterial;
  busy: boolean;
  error: string | null;
  blockers: Array<{ code: string; message: string }>;
};

type Deps = {
  onSummaryToast: (message: string) => void;
  onDeleted: () => void;
};

export function useMaterialPermanentDeleteFlow(deps: Deps) {
  const [pending, setPending] = useState<Pending | null>(null);

  const request = useCallback((material: MfgMaterial) => {
    if (material.is_active) return;
    setPending({ material, busy: false, error: null, blockers: [] });
  }, []);

  const cancel = useCallback(() => {
    setPending((prev) => (prev?.busy ? prev : null));
  }, []);

  const confirm = useCallback(async () => {
    const snapshot = pending;
    if (!snapshot) return;
    setPending({ ...snapshot, busy: true, error: null, blockers: [] });
    const res = await deleteMaterialApi({ material_id: snapshot.material.id });
    if (!res.ok) {
      const blockers =
        res.body &&
        typeof res.body === "object" &&
        res.body !== null &&
        "blockers" in res.body &&
        Array.isArray((res.body as { blockers: unknown }).blockers)
          ? ((res.body as { blockers: Array<{ code: string; message: string }> }).blockers ?? [])
          : [];
      setPending({
        ...snapshot,
        busy: false,
        error: res.error,
        blockers,
      });
      return;
    }
    deps.onSummaryToast(`Permanently deleted ${snapshot.material.name}`);
    deps.onDeleted();
    setPending(null);
  }, [pending, deps]);

  return { pending, request, confirm, cancel };
}
